import React, { useEffect, useState } from 'react'
import { Modal, Button, Divider } from 'antd'
import { CheckCircleFilled, CheckOutlined } from '@ant-design/icons'
import './styles.modules.scss'

type InstructionSection = {
  id: string
  title: string
  content?: string
  checklist?: string[]
}

type NotaryInstructionsProps = {
  sections: InstructionSection[]
}

export const NotaryInstructions: React.FC<NotaryInstructionsProps> = ({ sections }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [acknowledged, setAcknowledged] = useState<{ [key: number]: boolean }>({})

  const [callbackEventName, setCallbackEventName] = useState<string | null>(null)
  const [triggeringElement, setTriggeringElement] = useState<HTMLElement | null>(null)

  const SHOW_MODAL_EVENT = 'show-notary-instructions-modal'

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent
      setCallbackEventName(customEvent.detail.callbackEventName)
      setTriggeringElement(customEvent.detail.triggeringElement)
      setIsOpen(true)
    }

    window.addEventListener(SHOW_MODAL_EVENT, handler)
    return () => window.removeEventListener(SHOW_MODAL_EVENT, handler)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    setCallbackEventName(null)
    setTriggeringElement(null)
    setAcknowledged({})
  }

  const handleAccept = () => {
    if (callbackEventName) {
      window.dispatchEvent(
        new CustomEvent(callbackEventName, {
          detail: { triggeringElement }
        })
      )
    }
    handleClose()
  }

  const handleAcknowledge = (index: number) => {
    setAcknowledged((prev) => ({ ...prev, [index]: true }))
  }

  const allAcknowledged = sections.every((_, index) => acknowledged[index])

  return (
    <Modal
      title={<span className="modal-title">Acknowledge instructions</span>}
      open={isOpen}
      onCancel={handleClose}
      width={715}
      height={714}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="download" type="primary" disabled={!allAcknowledged} onClick={handleAccept}>
          Download documents
        </Button>
      ]}
      className="notary-instructions-modal"
    >
      <div className="modal-content-scrollable">
        <p>You must read and accept the following in order to download documents.</p>
        {sections.map((section, index) => (
          <div key={index} className={`section ${acknowledged[index] ? 'acknowledged' : ''}`}>
            <h4 className="section-title">{section.title}</h4>
            {section.content && <p>{section.content}</p>}
            {section.checklist && (
              <ul className="checklist">
                {section.checklist.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}

            <Divider />
            <Button
              type="link"
              className={`acknowledge-btn ${acknowledged[index] ? 'acknowledged' : ''}`}
              onClick={() => handleAcknowledge(index)}
            >
              {acknowledged[index] ? <CheckCircleFilled /> : <CheckOutlined />} I understand
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  )
}
